// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { __ } from '../../i18n/l10n';
import type { ScoreboardV2Props } from '../../states/scoreboards';
import theme from '../../styled-components/dark';

const Wrapper = styled.div`
  color: ${theme.History.outcome.match.color};
  font-size: 0.77em;
`;

const ScoreWrapper = styled.span`
  margin: 0 0.35em;
`;

const Score = styled.span`
  font-size: 1.07em;
  margin: 0 0.15em;

  ${({ $isLive }) => css`
    color: ${theme.History.outcome.match[$isLive ? 'liveScoreColor' : 'endedScoreColor']};
  `}
`;

const SecondaryScore = styled.span`
  font-size: 0.8em;
  margin-left: 0.4em;
  color: ${theme.History.outcome.match.endedScoreColor};
`;

const HistoryDefaultScoreboard = ({ competitors, scores, isLive }: ScoreboardV2Props) => {
  const competitor1 = useMemo(() => competitors[0].name, [competitors]);
  const competitor2 = useMemo(() => competitors[1].name, [competitors]);

  const aetScores = useMemo(() => {
    const { score: aetScore } = scores?.secondaries?.find(({ isAet }) => isAet) ?? {};
    return aetScore?.length > 1 ? aetScore : null;
  }, [scores]);

  const getScore = useCallback(
    (idx, aet) => {
      const aetScore = aet && aet[idx] ? parseInt(aet[idx], 10) : 0;

      return scores?.main?.score?.length >= idx + 1 ? parseInt(scores.main.score[idx], 10) - aetScore : null;
    },
    [scores],
  );

  const score1 = useMemo(() => getScore(0, aetScores), [getScore, aetScores]);
  const score2 = useMemo(() => getScore(1, aetScores), [getScore, aetScores]);
  const shouldDisplayScore = useMemo(
    () => score1 !== undefined && score1 !== null && score2 !== undefined && score2 !== null,
    [score1, score2],
  );

  const renderSecondaryScore = useCallback(() => {
    const { score: regScore } = scores?.secondaries?.find(({ isReg }) => isReg) ?? {};
    const { score: psoScore } = scores?.secondaries?.find(({ isPso }) => isPso) ?? {};

    const regText = regScore?.length > 1 ? __('%1$s-%2$s reg. time', [regScore[0], regScore[1]]) : null;
    const psoText = psoScore?.length > 1 ? __('%1$s-%2$s PSO', [psoScore[0], psoScore[1]]) : null;

    if (regText && psoText) {
      return (
        <SecondaryScore>
          ({regText} / {psoText})
        </SecondaryScore>
      );
    }
    if (regText) {
      return <SecondaryScore>({regText})</SecondaryScore>;
    }
    if (psoText) {
      return <SecondaryScore>({psoText})</SecondaryScore>;
    }

    if (aetScores) {
      const aetScore1 = getScore(0);
      const aetScore2 = getScore(1);
      const aetText = aetScore1 && aetScore2 ? __('%1$s-%2$s a.e.t.', [aetScore1, aetScore2]) : null;

      if (aetText) {
        return <SecondaryScore>({aetText})</SecondaryScore>;
      }
    }
  }, [scores, aetScores, getScore]);

  return (
    <Wrapper>
      {competitor1}
      {shouldDisplayScore ? (
        <ScoreWrapper>
          <Score $isLive={isLive}>{score1}</Score> - <Score $isLive={isLive}>{score2}</Score>
        </ScoreWrapper>
      ) : (
        ' - '
      )}
      {competitor2}
      {renderSecondaryScore()}
    </Wrapper>
  );
};

export default HistoryDefaultScoreboard;
